import {
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useAppController } from 'customHooks/useAppController'
import { isEqual } from 'lodash'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BoMItemActivityDto,
  IssueCode,
  ResourceMatchType,
  ResourceSummaryDto,
  WorkingStepDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { useBomItemIssueDescription } from '../components/useBomItemIssueDescription'
import { saveBomItemResource } from '../store/asyncActions/saveBomItemResource'
import { ProjectEditableStateSelector } from '../store/selectors/ProjectEditableStateSelector'
import { bomItemActivitiesSelectors } from '../store/selectors/activities/bomItemActivitiesSelectors'
import { projectSelectors } from '../store/selectors/projectSelectors'
import { JsonViewer } from 'components/Common/JsonViewer'

const AUTO_RESOURCE_ID = '00000000-0000-0000-0000-000000000000'

type Props = {
  bomItemPointersAndActivities: {
    bomItemPointer: BomItemPointer
    activityId?: string
  }[]
  workingStep?: WorkingStepDto
  activityWorkingStepType?: WorkingStepType
  /**
   * the specific bom item activity id this selector is for.
   * Required (together with useStoreWorkingStep) to correctly resolve the
   * right activity when a bom item has multiple activities of the same
   * working step type (e.g. two "Cleaning" activities)
   */
  bomItemActivityId?: string
  useStoreActivity?: boolean

  onFocus?: () => void
  onBlur?: () => void
  disableLabel?: boolean
  disableUnderline?: boolean
  disableMatchDescription?: boolean
  disableSelectMaterialError?: boolean
  fullWidth?: boolean
  typographyClassname?: string
  /**
   * onClick propagation, default to false (e.stopPropagation() is called in the onClick handler)
   */
  enableClickPropagation?: boolean
  withLabel?: boolean
  disabled?: boolean
}

const useStyles = makeStyles({ name: 'ResourceSelector' })(() => {
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      minHeight: '32px',
    },
    typographyDefault: {
      width: '100%',
      textAlign: 'left',
      maxWidth: '100%',
      whiteSpace: 'pre-wrap',
      display: 'block',
    },
    typograophyFullWidth: {
      maxWidth: '100%',
    },
  }
})

export const WorkingStepResourceSelector = (props: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { classes, cx } = useStyles()
  const isBuyer = useAppSelector(projectSelectors.projectInBuyerViewSelector)
  const firstBomItemPointer =
    props.bomItemPointersAndActivities[0]?.bomItemPointer
  const bomItemPointersAndActivityId = props.bomItemPointersAndActivities.map(
    ({ bomItemPointer, activityId }) => ({
      bomItemPointer,
      activityId,
    }),
  )

  const workingStepFromStore = useAppSelector(
    (props.useStoreActivity &&
      bomItemActivitiesSelectors.byWorkingStep(
        firstBomItemPointer,
        props.activityWorkingStepType,
        props.bomItemActivityId,
      )) ||
    (() => undefined),
    isEqual,
  )

  const activity: BoMItemActivityDto = props.useStoreActivity
    ? workingStepFromStore || props.workingStep
    : props.workingStep

  const materialSummary = useAppSelector(
    bomItemActivitiesSelectors.articlesPerWorkingStep(
      firstBomItemPointer,
      activity?.secondaryWorkingStep ?? props.activityWorkingStepType,
      props.bomItemActivityId,
    ),
    isEqual,
  )

  const [open, setOpen] = useState(false)

  const projectEditable = useAppSelector(ProjectEditableStateSelector)

  const { partyId, projectId } = useOrganizationAndProjectContext()
  const { controller, loading, error } = useAppController(
    () => new BomItemController(partyId, projectId),
  )

  const [resourceOptions, setResourceOptions] =
    useState<Array<ResourceSummaryDto>>()
  const [pendingResource, setPendingResource] = useState<ResourceSummaryDto>()
  const [savingResource, setSavingResource] = useState(false)
  const savingRef = useRef(false)

  const resolvedActivityId =
    props.bomItemActivityId ||
    workingStepFromStore?.id ||
    bomItemPointersAndActivityId[0]?.activityId
  const workingStepType =
    activity?.secondaryWorkingStep ?? props.activityWorkingStepType
  const autoResource: ResourceSummaryDto = resourceOptions?.find(
    (x) => x.id === AUTO_RESOURCE_ID,
  ) ?? {
    id: AUTO_RESOURCE_ID,
    name: t('project:auto-select-resource', 'auto select resource'),
    workingStepType,
    isOutsourced: false,
  }
  const selectableResourceOptions = [
    ...(resourceOptions?.filter((x) => x.id !== AUTO_RESOURCE_ID) ?? []),
    autoResource,
  ]
  const selectableResourcesLoadingKey = `get-selectable-resources-${firstBomItemPointer?.id}-${workingStepType}`
  const saveResourceLoadingKey = `set-resource-${firstBomItemPointer?.id}-${resolvedActivityId}`
  const loadingResources = loading[selectableResourcesLoadingKey]
  const savingResourceInController = loading[saveResourceLoadingKey]
  const selectedResource = pendingResource ?? activity?.resource
  const selectedResourceId = selectedResource?.id

  const resourceDataLoading = Boolean(
    materialSummary?.id &&
    !selectedResourceId &&
    activity?.resource?.matchType !== ResourceMatchType.NoCapableResources,
  )
  const materialRequiredBeforeResourceSelection = Boolean(
    firstBomItemPointer?.type === BomItemType.partType &&
    !materialSummary?.id &&
    !props.disableSelectMaterialError &&
    !selectedResourceId,
  )
  const resourceSelectorVisible = Boolean(
    !materialRequiredBeforeResourceSelection && activity?.resource?.matchType &&
    (activity?.id || selectedResourceId || resourceDataLoading),
  )

  useEffect(() => {
    if (
      pendingResource?.id &&
      pendingResource.id === activity?.resource?.id &&
      !savingResourceInController
    ) {
      setPendingResource(undefined)
      setSavingResource(false)
    }
  }, [pendingResource?.id, savingResourceInController, activity?.resource?.id])

  const handleChange = async (e: SelectChangeEvent<string>) => {
    if (savingRef.current) {
      return
    }

    const resource = selectableResourceOptions.find(
      (x) => x.id === e.target.value,
    )

    if (!resource) {
      return
    }

    const isAutoResource = resource.id === AUTO_RESOURCE_ID

    setPendingResource(isAutoResource ? undefined : resource)
    setSavingResource(true)
    savingRef.current = true

    await dispatch(
      saveBomItemResource({
        bomItemPointersAndActivityId: bomItemPointersAndActivityId
          .map((bomItemPointerAndActivity) => ({
            ...bomItemPointerAndActivity,
            activityId:
              bomItemPointerAndActivity.activityId ||
              (bomItemPointersAndActivityId.length === 1
                ? resolvedActivityId
                : undefined),
          }))
          .filter((target) => target.activityId),
        resource,
        controllerInstance: controller,
      }),
    )
      .unwrap()
      .finally(() => {
        savingRef.current = false
        setSavingResource(false)

        if (isAutoResource) {
          setPendingResource(undefined)
        }
      })
  }

  const getHelperText = useCallback(() => {
    if (!activity?.id) {
      return null
    }

    let helperText =
      error[`get-selectable-resources-${firstBomItemPointer}`]?.message

    if (resourceDataLoading && resourceSelectorVisible) {
      helperText = t('project:loading-resource-data', 'loading resource data')
    }

    if (!helperText && materialRequiredBeforeResourceSelection) {
      helperText = t(
        'project:select-material-before-selecting-resource',
        'select a material group or quality to check available resources',
      )
    }

    if (!helperText && !props.disableMatchDescription) {
      helperText = t(activity?.resource?.matchDescription)
    }

    return helperText
  }, [activity?.id, activity?.resource?.matchDescription, error, firstBomItemPointer, resourceDataLoading, resourceSelectorVisible, materialRequiredBeforeResourceSelection, props.disableMatchDescription, t])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!props.enableClickPropagation) {
        e.stopPropagation()
      }

      if (
        resourceDataLoading ||
        props.disabled ||
        !projectEditable ||
        activity?.resource?.matchType === ResourceMatchType.OneCapableResource
      ) {
        return
      }

      if (!open) {
        controller
          .GetBomItemSelectableResources(firstBomItemPointer, workingStepType)
          .then((summaries) => {
            setResourceOptions(
              summaries?.filter((x) => x.id !== activity?.resource?.id),
            )
          })
      }

      setOpen(!open)
    },
    [
      controller,
      open,
      projectEditable,
      firstBomItemPointer,
      props.enableClickPropagation,
      props.disabled,
      resourceDataLoading,
      activity?.resource?.id,
      activity?.resource?.matchType,
      workingStepType,
    ],
  )

  const { getIssueDescription } = useBomItemIssueDescription()
  const resourceNotSetIssue = useAppSelector(
    projectSelectors.bomItemIssueByCodeSelector(
      firstBomItemPointer,
      IssueCode.ResourceNotSet,
    ),
  )

  if (isBuyer) {
    return null
  }

  return (
    <span className={classes.root}>
      {resourceNotSetIssue && activity?.['id'] && !activity?.resource?.id && (
        <Typography variant="caption" color="error">
          {getIssueDescription(resourceNotSetIssue)}
        </Typography>
      )}
      {resourceSelectorVisible && (
        <>
          {props.withLabel && (
            <LocalizedTypography
              translationKey="project:resource"
              variant="caption"
              color="textSecondary"
            />
          )}
          {/* <JsonViewer data={activity} /> */}
          <Select
            value={selectedResource?.id || 'loading'}
            required
            onChange={handleChange}
            label={props.disableLabel ? null : t('project:resource')}
            open={open}
            fullWidth
            onClick={handleClick}
            onClose={() => {
              setOpen(false)
              setResourceOptions([])
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            disableUnderline={props.disableUnderline}
            disabled={
              //resourceDataLoading ||
              props.disabled ||
              activity?.resource?.matchType ===
              ResourceMatchType.OneCapableResource ||
              !projectEditable ||
              (selectedResource?.id && !selectedResource?.name) ||
              savingResource ||
              savingResourceInController
            }
            MenuProps={{
              disablePortal: false,
              style: { zIndex: 10000 },
            }}
            renderValue={() => {
              if (
                selectedResource?.id &&
                (!selectedResource.name || selectedResource.name === '')
              ) {
                return (
                  <LocalizedTypography
                    translationKey="project:resource-info-not-available"
                    variant="inherit"
                    component="span"
                  >
                    resource info not available
                  </LocalizedTypography>
                )
              }

              return (
                <Typography
                  variant="inherit"
                  // variant={workingStep?.resource?.id ? 'caption' : 'caption'}
                  color={
                    activity?.resource?.id ? 'textPrimary' : 'textSecondary'
                  }
                  component="span"
                  className={cx(
                    classes.typographyDefault,
                    props.typographyClassname,
                    {
                      [classes.typograophyFullWidth]: props.fullWidth,
                    },
                  )}
                >
                  {resourceDataLoading && (
                    <span
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <CircularProgress
                        size={12}
                        sx={{ marginRight: 1, verticalAlign: 'middle' }}
                      />
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        component="span"
                      >
                        loading resource data
                      </Typography>
                    </span>
                  )}

                  {!resourceDataLoading &&
                    (savingResource || savingResourceInController) && (
                      <span
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                        }}
                      >
                        <CircularProgress
                          size={12}
                          sx={{ marginRight: 1, verticalAlign: 'middle' }}
                        />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          component="span"
                        >
                          saving resource selection
                        </Typography>
                      </span>
                    )}
                  {!resourceDataLoading &&
                    !savingResource &&
                    !savingResourceInController &&
                    selectedResource?.name && (
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        component="span"
                      >
                        {selectedResource.name}
                      </Typography>
                    )}
                </Typography>
              )
            }}
          >
            {(loadingResources || resourceDataLoading) && (
              <MenuItem value="loading">
                <Typography variant="caption">
                  <CircularProgress
                    size={12}
                    sx={{ marginRight: 1, verticalAlign: 'middle' }}
                  />
                  {`${t('common:loading')}...`}
                </Typography>
              </MenuItem>
            )}
            {
              // this one is here to not have the @mui runtime warning
              // about current value not being in the options.
              // notice the display: none to not show it
              selectedResource?.id && (
                <MenuItem
                  value={selectedResource.id}
                  style={{ display: 'none' }}
                  sx={{
                    borderBottom: (theme) =>
                      `2px solid ${theme.palette.divider}`,
                    gap: '8px',
                  }}
                >
                  <Typography variant="caption">
                    {selectedResource.name}
                  </Typography>
                </MenuItem>
              )
            }
            {selectableResourceOptions
              ?.filter((x) => x.id !== selectedResource?.id)
              .map((x) => {
                return (
                  <MenuItem key={x.id} value={x.id}>
                    <Typography variant="caption">{x.name}</Typography>
                  </MenuItem>
                )
              })}
          </Select>
        </>
      )}
      <Typography variant="caption" color="textSecondary">
        {getHelperText()}
      </Typography>
    </span>
  )
}
